import React, { useEffect, useState } from "react";
import SelectInput from "../Components/SelectInput";
import {
  setDocData,
  getDocDataSnap,
} from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import CloseButton from "../../../components/CloseButton";
import TaskAssignmentModule from "../../../components/TaskAssignmentModule";
import HistoryModule from "../../../components/HistoryModule";
import CommentBlock from "../../../components/CommentBlock";
import {
  createConcernStatusNotification,
  createConcernCommentNotification,
  createConcernAssignmentNotification,
  createConcernResolutionNotification,
  sendConcernNotification,
} from "../../../utils/concernNotifications";
import { dualNotification } from "../../../utils/EleghoUtils";
import IOSSpinner from "../../../components/IOSSpinner";
import { motion, AnimatePresence } from "framer-motion";
import ReactDOM from "react-dom";
import {
  FiEdit2,
  FiX,
  FiEye,
  FiEyeOff,
  FiMessageSquare,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiSend,
  FiTrash2,
  FiImage,
  FiVideo,
} from "react-icons/fi";

// Portal component for modals
const Portal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? ReactDOM.createPortal(children, document.body) : null;
};

export default function ConcernsPageDetails({ concern, setViewMode }) {
  const [formData, setFormData] = useState(concern);
  const [comments, setComments] = useState(concern.comments || []);
  const [newComment, setNewComment] = useState("");
  const [dispatchee, setDispatchee] = useState(concern?.dispatchee || "");
  const [newStatus, setNewStatus] = useState(concern.status);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [department, setDepartment] = useState("");
  const [resolution, setResolution] = useState(concern.resolution || "");
  const [editMode, setEditMode] = useState(false);
  const [assignedToArray, setAssignedToArray] = useState(
    concern.assignedTo || []
  );
  const [updateHappened, setUpdateHappened] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [resolutionSaving, setResolutionSaving] = useState(false);
  const [savingComment, setSavingComment] = useState(false);

  const currentUser = useSelector((state) => state.userObject);

  useEffect(() => {
    const fetchConcern = async () => {
      await getDocDataSnap("concerns", concern.id, (data) => {
        setFormData(data);
        setComments(data.comments || []);
        setDispatchee(data?.dispatchee || "");
        setIsLoading(false);
      });
    };
    fetchConcern();
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const dispatchOptions = [
    {
      label: "Engineering",
      value: "Engineering",
    },
    {
      label: "Security",
      value: "Security",
    },
    {
      label: "Admin",
      value: "Admin",
    },
    {
      label: "Finance",
      value: "Finance",
    },
    {
      label: "Facilities",
      value: "Facilities",
    },
  ];

  const statusList = ["Pending", "Processing", "Dispatched", "Resolved"];

  const statusOptions = statusList.map((item) => {
    return {
      label: item,
      value: item,
    };
  });

  const handleDispatch = async () => {
    setIsProcessing(true);
    const userEmail =
      formData.email ||
      formData.user ||
      (formData.createdBy && formData.createdBy.includes("@")
        ? formData.createdBy
        : null);

    const docObject = {
      ...formData,
      status: "Dispatched",
      dispatchee: department,
      updatedAt: new Date().toLocaleString(),
      updatedBy: currentUser.uid,
      updatedName: currentUser.displayName,
      updatedEmail: currentUser.email,
      email: userEmail,
      updateType: `Dispatched to ${department}`,
    };

    await setDocData("concerns", concern.id, docObject);

    // Send notification to requestor
    const notification = createConcernStatusNotification(
      concern.id,
      "Dispatched",
      formData.concern,
      formData.identifier,
      currentUser.displayName
    );

    await sendConcernNotification(docObject, notification);

    setIsProcessing(false);
    toast.success(`Dispatched to ${department}`);
  };

  const handleSelectDepartment = async (selected) => {
    setDepartment(selected);
  };

  const handleSelectStatus = (selected) => {
    setFormData({ ...formData, status: selected.value });
    setNewStatus(selected.value);
    setUpdateHappened(true);
  };

  const handleUpdateStatus = async () => {
    setStatusUpdateLoading(true);
    let docObject = {};
    const userEmail =
      formData.email ||
      formData.user ||
      (formData.createdBy && formData.createdBy.includes("@")
        ? formData.createdBy
        : null);

    if (newStatus === "Resolved") {
      //check if resolution is empty
      if (!formData.resolution || formData.resolution.trim() === "") {
        toast.error(
          "Please add a resolution report before resolving the concern."
        );
        setStatusUpdateLoading(false);
        return;
      }

      docObject = {
        ...formData,
        status: newStatus,
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        updatedAt: new Date().toLocaleString(),
        resolvedAt: new Date().toLocaleString(),
        email: userEmail,
        history: [
          ...(formData?.history || ""),
          {
            action: "Status is Updated to ",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: newStatus,
          },
        ],
      };
    } else {
      docObject = {
        ...formData,
        status: newStatus,
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        updatedAt: new Date().toLocaleString(),
        email: userEmail,
        history: [
          ...(formData?.history || ""),
          {
            action: "Status is Updated to ",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: newStatus,
          },
        ],
        resolvedAt: "Not yet resolved",
      };
    }

    await setDocData("concerns", concern.id, docObject);

    // Send notification to requestor
    const notificationObject = {
      recipient: formData.email,
      recipientName: formData.createdBy,
      sender: currentUser.email,
      senderName: currentUser.displayName,
      subject: "Concern Status Update",
      message: `Your concern status has been updated to ${newStatus}`,
      type: "concern status update",
      id: concern.id,
      url: `/concern/${concern.id}`,
      read: false,
    };

    await dualNotification(notificationObject);

    toast.success("Status Successfully Updated");
    setUpdateHappened(false);
    setStatusUpdateLoading(false);
  };

  const handleNewComment = (e) => {
    setNewComment(e.target.value);
  };

  const handleAddComment = async () => {
    setSavingComment(true);
    const userEmail =
      formData.email ||
      formData.user ||
      (formData.createdBy && formData.createdBy.includes("@")
        ? formData.createdBy
        : null);

    const commentObject = {
      message: newComment,
      createdBy: currentUser.displayName,
      createdAt: new Date().toLocaleString(),
      email: currentUser.email,
    };

    const newHistoryItem = {
      action: "Comment Added",
      at: new Date().toLocaleString(),
      by: currentUser.displayName,
      to: newComment,
    };

    // Create a complete updated document object to ensure all fields are preserved
    const updatedDoc = {
      ...formData,
      email: userEmail, // Ensure email is preserved
      comments: [...comments, commentObject],
      history: [...(formData?.history || ""), newHistoryItem],
    };

    await setDocData("concerns", concern.id, updatedDoc);

    // Send notification to requestor
    const notificationObject = {
      recipient: formData.email,
      recipientName: formData.createdBy,
      sender: currentUser.email,
      senderName: currentUser.displayName,
      subject: "Concerns - Comment Added",
      message: `A comment has been added to your concern`,
      type: "comment added",
      id: concern.id,
      url: `/concern/${concern.id}`,
      read: false,
    };

    await dualNotification(notificationObject);

    toast.success("Comment Successfully Added");
    setNewComment("");
    setComments([...comments, commentObject]);
    setSavingComment(false);
  };

  const handleChangeResolution = (e) => {
    const { value } = e.target;
    setFormData({ ...formData, resolution: value });
  };

  const handleResolutionUpdate = async () => {
    if (editMode) {
      setResolutionSaving(true);
      const userEmail =
        formData.email ||
        formData.user ||
        (formData.createdBy && formData.createdBy.includes("@")
          ? formData.createdBy
          : null);

      const docObject = {
        ...formData,
        updatedAt: new Date().toLocaleString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        updatedEmail: currentUser.email,
        email: userEmail,
        updateType: "Resolution is Updated",
        history: [
          ...(formData?.history || ""),
          {
            action: "Resolution report is updated ",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: formData.resolution || "No Resolution Report",
          },
        ],
      };

      await setDocData("concerns", concern.id, docObject);

      // Send notification to requestor
      const notificationObject = {
        recipient: formData.email,
        recipientName: formData.createdBy,
        sender: currentUser.email,
        senderName: currentUser.displayName,
        subject: "Resolution Report Update",
        message: `Your concern resolution report has been updated`,
        type: "resolution update",
        id: concern.id,
        url: `/concern/${concern.id}`,
        read: false,
      };

      await dualNotification(notificationObject);

      toast.success("Resolution is Updated");
      setResolutionSaving(false);
    }
  };

  const handleDeleteComment = async (index) => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      const userEmail =
        formData.email ||
        formData.user ||
        (formData.createdBy && formData.createdBy.includes("@")
          ? formData.createdBy
          : null);

      const newComments = comments.filter((item, i) => i !== index);

      // Create a new history item
      const newHistoryItem = {
        action: "Comment Deleted",
        at: new Date().toLocaleString(),
        by: currentUser.displayName,
        to: "Comment was deleted",
      };

      // Create a complete updated document object to ensure all fields are preserved
      const updatedDoc = {
        ...formData,
        email: userEmail, // Ensure email is preserved
        comments: newComments,
        history: [...(formData?.history || ""), newHistoryItem],
      };

      await setDocData("concerns", concern.id, updatedDoc);

      // Update local state
      setComments(newComments);
      setFormData(updatedDoc);

      toast.success("Comment Successfully Deleted");
    }
  };

  const handleAssignment = async () => {
    if (editMode) {
      setAssignmentSaving(true);
      const userEmail =
        formData.email ||
        formData.user ||
        (formData.createdBy && formData.createdBy.includes("@")
          ? formData.createdBy
          : null);

      const docObject = {
        ...formData,
        updatedAt: new Date().toLocaleString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        updatedEmail: currentUser.email,
        email: userEmail,
        updateType: "Concern Details is Updated",
        assignedTo: assignedToArray,
        history: [
          ...(formData?.history || ""),
          {
            action:
              assignedToArray.length === 0
                ? "Unassigned"
                : "Concern is Assigned to",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: assignedToArray.map((item) => item.label).join(", "),
          },
        ],
      };

      await setDocData("concerns", concern.id, docObject);
      // Send notifications to newly assigned users
      const previouslyAssigned = new Set(
        (formData.assignedTo || []).map((user) => user.value)
      );
      const newlyAssigned = assignedToArray
        .filter((user) => !previouslyAssigned.has(user.value))
        .map((user) => user.value);

      if (newlyAssigned.length > 0) {
        await Promise.all(
          newlyAssigned.map(async (userEmail) => {
            const notificationObject = {
              recipient: userEmail,
              recipientName: userEmail,
              sender: currentUser.email,
              senderName: currentUser.displayName,
              subject: "Resident Concern Assigned",
              message: `You have been assigned a new concern`,
              type: "concern assignment",
              id: formData.id,
              concern: formData.concern,
              status: formData.status,
              url: `/property-management?type=Concerns`,
              read: "false",
            };

            await dualNotification(notificationObject);
          })
        );

        //notification to user
        const notificationObject = {
          recipient: formData.user,
          recipientName: formData.createdBy,
          sender: currentUser.email,
          senderName: currentUser.displayName,
          subject: "Concern Assignment",
          message: `Youre concern has been assigned`,
          type: "concern assignment",
          id: formData.id,
          concern: formData.concern,
          status: formData.status,
          url: `/concern/${formData.id}`,
          read: "false",
        };

        await dualNotification(notificationObject);
      }

      toast.success("Concern Details is Updated");
      setEditMode(false);
      setAssignmentSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData(concern);
    setAssignedToArray(concern.assignedTo || []);
    toast.error("Edit Cancelled");
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return <FiClock className="w-4 h-4" />;
      case "processing":
        return <FiAlertCircle className="w-4 h-4" />;
      case "dispatched":
        return <FiSend className="w-4 h-4" />;
      case "resolved":
        return <FiCheckCircle className="w-4 h-4" />;
      default:
        return <FiAlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "processing":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "dispatched":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "resolved":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const allowedToEdit = ["admin", "super-admin", "security"];

  return (
    <div className="min-h-screen p-4 sm:p-6  w-full ">
      <div className="max-w-7xl mx-auto relative">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
            <div className="text-base sm:text-lg font-semibold text-[#363062]">
              Concern Details
            </div>
            <div className="flex items-center gap-2 sm:gap-4 mr-12">
              {allowedToEdit.some((role) =>
                currentUser.roles.includes(role)
              ) && (
                <button
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    editMode
                      ? "bg-red-500 text-white hover:bg-red-600 shadow-sm"
                      : "bg-[#363062] text-white hover:bg-opacity-90 shadow-sm"
                  }`}
                  onClick={() => {
                    if (editMode) {
                      handleCancelEdit();
                    } else {
                      setEditMode(true);
                    }
                  }}
                >
                  {editMode ? (
                    <>
                      <FiX className="w-4 h-4" />
                      Cancel Edit
                    </>
                  ) : (
                    <>
                      <FiEdit2 className="w-4 h-4" />
                      Edit Concern
                    </>
                  )}
                </button>
              )}

              {setViewMode !== undefined && (
                <button
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors absolute top-2 right-2"
                  onClick={() => setViewMode(false)}
                >
                  <FiX className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 sm:mb-6">
            <div
              className={`px-3 py-1.5 rounded-full border flex items-center gap-2 ${getStatusColor(
                formData.status
              )} font-medium capitalize text-xs sm:text-sm`}
            >
              {getStatusIcon(formData.status)}
              {formData.status}
            </div>
            {editMode && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <SelectInput
                  options={statusOptions}
                  onChange={(selected) => handleSelectStatus(selected)}
                  value={statusOptions.find(
                    (item) => item.value === formData.status
                  )}
                  className="w-full sm:w-48"
                />
                <button
                  className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center gap-2 text-sm"
                  disabled={!updateHappened || statusUpdateLoading}
                  onClick={handleUpdateStatus}
                >
                  {statusUpdateLoading ? (
                    <>
                      <IOSSpinner className="w-4 h-4" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    "Update Status"
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left Column - Concern Details */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Concern Information Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    Concern Information
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Reference Number
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {concern.id.slice(-8)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Type of Concern
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData.concern}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Date Filed
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData.createdAt}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Created by
                      </div>
                      <div className="font-medium text-xs sm:text-sm">
                        {formData.createdBy}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 sm:mb-6">
                    <div className="text-xs sm:text-sm text-gray-500 mb-1">
                      Description
                    </div>
                    <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 text-xs sm:text-sm">
                      {formData.description}
                    </div>
                  </div>

                  {/* Media Section */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Image
                      </div>
                      <div className="flex justify-center items-center h-24 sm:h-32 bg-gray-50 rounded-lg border border-gray-100">
                        {formData.image ? (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowImageModal(true)}
                            className="px-3 sm:px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium flex items-center gap-2 text-xs sm:text-sm"
                          >
                            <FiImage className="w-4 h-4" />
                            View Image
                          </motion.button>
                        ) : (
                          <div className="text-gray-500 italic text-xs sm:text-sm">
                            No image uploaded
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">
                        Video
                      </div>
                      <div className="flex justify-center items-center h-24 sm:h-32 bg-gray-50 rounded-lg border border-gray-100">
                        {formData.video ? (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowVideoModal(true)}
                            className="px-3 sm:px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium flex items-center gap-2 text-xs sm:text-sm"
                          >
                            <FiVideo className="w-4 h-4" />
                            View Video
                          </motion.button>
                        ) : (
                          <div className="text-gray-500 italic text-xs sm:text-sm">
                            No video uploaded
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062] flex items-center gap-2">
                    <FiMessageSquare className="w-4 h-4" />
                    Comments
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="mb-4 sm:mb-6">
                    <textarea
                      name="comment"
                      className="w-full rounded-lg p-3 sm:p-4 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-transparent resize-none text-xs sm:text-sm"
                      rows="3"
                      onChange={handleNewComment}
                      placeholder="Write your comment here..."
                      value={newComment}
                    ></textarea>
                    <div className="flex justify-end mt-3">
                      <button
                        className="px-3 sm:px-4 py-2 bg-[#8FCD00] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center gap-2 text-xs sm:text-sm"
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                      >
                        {savingComment ? (
                          <>
                            <IOSSpinner className="w-4 h-4" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <FiSend className="w-4 h-4" />
                            Save Comment
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {comments.length > 0 ? (
                      comments.map((comment, index) => (
                        <CommentBlock
                          key={index}
                          comment={comment}
                          currentUser={currentUser}
                          handleDeleteComment={() => handleDeleteComment(index)}
                        />
                      ))
                    ) : (
                      <div className="text-gray-500 italic p-4 bg-gray-50 rounded-lg text-center text-xs sm:text-sm">
                        No comments yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Assignment & History */}
            <div className="space-y-4 sm:space-y-6">
              {/* Assignment Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    Task Assignment
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <TaskAssignmentModule
                    assignedToArray={assignedToArray}
                    setAssignedToArray={setAssignedToArray}
                    editMode={editMode}
                  />
                  {editMode && (
                    <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-end">
                      <button
                        className="px-3 sm:px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm flex items-center gap-2 text-xs sm:text-sm disabled:opacity-70"
                        onClick={handleAssignment}
                        disabled={assignmentSaving}
                      >
                        {assignmentSaving ? (
                          <>
                            <IOSSpinner className="w-4 h-4" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          "Save Assignment"
                        )}
                      </button>
                      <button
                        className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-xs sm:text-sm"
                        onClick={handleCancelEdit}
                        disabled={assignmentSaving}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Resolution Report */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-sm sm:text-base text-[#363062]">
                    Resolution Report
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  <textarea
                    name="resolution"
                    className="w-full rounded-lg p-3 sm:p-4 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-transparent resize-none text-xs sm:text-sm"
                    rows="6"
                    onChange={handleChangeResolution}
                    placeholder="Add resolution report here..."
                    value={formData?.resolution || ""}
                    disabled={!editMode}
                  ></textarea>
                  {editMode && (
                    <div className="mt-4 flex justify-end">
                      <button
                        className="px-3 sm:px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm flex items-center gap-2 text-xs sm:text-sm disabled:opacity-70"
                        onClick={handleResolutionUpdate}
                        disabled={resolutionSaving}
                      >
                        {resolutionSaving ? (
                          <>
                            <IOSSpinner className="w-4 h-4" />
                            <span>Updating...</span>
                          </>
                        ) : (
                          "Update Resolution"
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* History Card */}
              <HistoryModule data={formData.history || []} />
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {showImageModal && formData.image && (
          <Portal>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90"
              onClick={() => setShowImageModal(false)}
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: "spring", damping: 20 }}
                className="relative z-[10000] max-w-5xl w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="absolute top-[-40px] sm:top-[-50px] right-0 bg-white bg-opacity-20 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-40 transition-colors"
                  onClick={() => setShowImageModal(false)}
                >
                  <FiX className="w-4 h-4 sm:w-5 sm:h-5" />
                </motion.button>
                <img
                  src={formData.image}
                  alt="Concern Image"
                  className="w-full h-auto max-h-[80vh] sm:max-h-[85vh] object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                  onError={() => {
                    toast.error("Failed to load image");
                    setShowImageModal(false);
                  }}
                />
              </motion.div>
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && formData.video && (
          <Portal>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90"
              onClick={() => setShowVideoModal(false)}
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: "spring", damping: 20 }}
                className="relative z-[10000] max-w-5xl w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="absolute top-[-40px] sm:top-[-50px] right-0 bg-white bg-opacity-20 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-40 transition-colors"
                  onClick={() => setShowVideoModal(false)}
                >
                  <FiX className="w-4 h-4 sm:w-5 sm:h-5" />
                </motion.button>
                <video
                  src={formData.video}
                  controls
                  className="w-full h-auto max-h-[80vh] sm:max-h-[85vh] rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                  onError={() => {
                    toast.error("Failed to load video");
                    setShowVideoModal(false);
                  }}
                />
              </motion.div>
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </div>
  );
}
